/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle
} from 'react';
import cloneDeep from 'lodash.clonedeep';
import isEmpty from 'lodash.isempty';
import isEqual from 'lodash.isequal';
import isUndefined from 'lodash.isundefined';
import {resolveDeviceName} from '@ion/core/src/entities/Devices';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {loadAssets} from '@ion/core/src/services/Connect';
import {entityId, setRel} from '@ion/core/src/services/ConnectUtils';
import {
  Assets,
  BulkActionEntity,
  CTCEntity,
  GenericObject,
  Groups
} from '@ion/core/src/types/entities';
import {sortByString} from '@ion/core/src/utils/ArrayUtil';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import styles from '@ion/dls/src/editors/editorStyles';
import EmbeddedForm from '@ion/dls/src/editors/forms/EmbeddedForm';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {useFormSave} from '@ion/dls/src/hooks/useFormSave';
import {FormState} from '@ion/dls/src/hooks/useFormState';
import {useGroupAssignmentDetailsForm} from '@ion/dls/src/hooks/useGroupAssignmentDetailsForm';
import {UserNotificationDialog} from '@ion/dls/src/notifications/UserNotificationDialog';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {useLoadDevices} from '@ion/query/src/hooks/useLoadDevices';
import {useNewGroup} from '@ion/query/src/hooks/useNewGroup';
import {GroupAssignmentAccountChooser} from '../bulk/GroupAssignmentAccountChooser';
import {GroupAssignmentActionChooser} from '../bulk/GroupAssignmentActionChooser';
import {GroupAssignmentActions} from '../bulk/types';
import {useGroupAssignment} from '../bulk/useGroupAssignment';
import {useCurrentGlobalGroup} from '../hooks/useCurrentGlobalGroup';
import {useCurrentGlobalGroupId} from '../hooks/useCurrentGlobalGroupId';
import {useSanitizeAccount} from '../hooks/useSanitizeAccount';
import {
  flattenGroupsList,
  getUniqGroups
} from '../pages/admin/groups/BulkEditGroupUtils';

const useStyles = (theme: DLSTheme) => ({
  errorMessage : {
    color      : dlsColor('caRed'),
    fontSize   : '12px',
    fontFamily : 'Roboto',
    fontWeight : 400
  }
});

export interface GroupAssignmentDetailsFormProps {
  areTotalRowsSelected: boolean;
  identifierType: string;
  setTaskNickNameAction: (action: string) => void;
  onSave: (details: GroupAssignmentDetails) => void;
  selectedRowEntities: CTCEntity[];
  isSelectedGridList?: boolean;
  entitySelectionFormState?: GenericObject;
  bulkImportParams?: GenericObject;
  entity?: BulkActionEntity;
  omitExtensions?: boolean;
}

export interface GroupAssignmentFormProps {
  allGroups?: Groups;
  isGroupsLoading?: boolean;
}

export type GroupAssignmentDetails = {
  addToGroup?: number[];
  removeAllGroups?: boolean;
  removeFromGroup?: number[];
  replaceGroup?: [{
    from: number;
    to: number;
  }];
}

export enum GroupAssignmentDetailsFields {
  ADD_GROUP = 'add',
  REMOVE_GROUP = 'remove',
  REPLACE_TO_GROUP = 'to',
  REPLACE_FROM_GROUP = 'from',
  ACCOUNT = 'account',
  NAME = 'name',
  NOTES = 'notes',
  ADD_NEW_GROUP = 'ADD NEW GROUP'
}

/**
 * Re-usable form with the details for group assignment within an entity.
 */
export const GroupAssignmentDetailsForm = forwardRef(({
  entity,
  identifierType,
  areTotalRowsSelected,
  setTaskNickNameAction,
  onSave,
  selectedRowEntities,
  entitySelectionFormState,
  bulkImportParams,
  isSelectedGridList = false,
  omitExtensions = false
}: GroupAssignmentDetailsFormProps, ref) => {
  const editorStyles        = useExtendStyles(styles, useStyles);
  const classes             = editorStyles();
  const globalGroup         = useCurrentGlobalGroup();
  const account             = useSanitizeAccount(globalGroup);
  const {createGroup}       = useNewGroup();
  const accountId           = useCurrentGlobalGroupId();
  const {loadDevices}       = useLoadDevices();
  const {
    groupAction,
    handleActionChooser,
    isNewGroup,
    setReplaceGroups,
    replaceGroups,
    handleRemoveGroupChooser,
    handleFromGroupChooser,
    fromGroup,
    toGroup,
    handleAccountGroupChooser,
    handleGroupChooser,
    isGroupNameEmpty,
    isGroupNameExists,
    groupName,
    groupNotes,
    setNoGroups,
    noGroups,
    setShowGroupsError,
    showGroupsError,
    setFormState,
    formState,
    fromAccount,
    handleGroupNameChooser,
    handleGroupNotesChooser,
    setIsGroupEmpty,
    isGroupEmpty,
    setIsFromGroupEmpty,
    isFromGroupEmpty
  } = useGroupAssignment({setTaskNickNameAction});

  useEffect(() => {
    if (selectedRowEntities?.length) {
      const devices = cloneDeep(selectedRowEntities)?.map((device) => {
        device.name = resolveDeviceName(device);
        return device;
      });
      getGroupFromEntities(devices, false);
    } else if (!isEmpty(entitySelectionFormState?.entityIdentifiers?.value)) {
      const value = entitySelectionFormState?.entityIdentifiers?.value;
      if (checkEntity(BulkActionEntity.DEVICES)) {
        getGroupsFromDevicesIdentifiers(value);
      } else if (checkEntity(BulkActionEntity.ASSETS)) {
        getGroupsFromAssetsIdentifiers(value);
      }
    } else {
      if (checkEntity(BulkActionEntity.DEVICES)) {
        getGroupsFromDevicesIdentifiers(bulkImportParams?.items?.map(({
          deviceIdentifier
        }) => deviceIdentifier));
      } else if (checkEntity(BulkActionEntity.ASSETS)) {
        getGroupsFromAssetsIdentifiers(bulkImportParams?.items?.map(({
          assetIdentifier
        }) => assetIdentifier));
      }
    }
  }, [selectedRowEntities, entitySelectionFormState, bulkImportParams]);

  useEffect(() => {
    checkGroups();
  }, [groupAction, replaceGroups]);

  const checkEntity = useCallback((entityType) => {
    if (isEqual(entity, entityType)) {
      return true;
    }
  }, [entity]);

  const getGroupsFromDevicesIdentifiers = useCallback((identifiers) => {
    try {
      loadDevices({
        hierLevel : true,
        pgSize    : 100,
        extension : 'groups',
        accountId,
        identifiers,
        omitExtensions,
        entity,
        identifierType
      }, {
        onSuccess : (device) => {
          if (device?.length) {
            getGroupFromEntities(device, true);
          } else {
            setShowGroupsError(true);
          }
        },
        onError : () => {
          setShowGroupsError(true);
        }
      });
    } catch (err) {
      setShowGroupsError(true);
    }
  }, [accountId]);

  const getGroupsFromAssetsIdentifiers = useCallback(async(identifiers) => {
    try {
      let assets: Assets = [];
      const results = await loadAssets({
        filterOn : {
          [identifierType] : `*${identifiers?.join(',')}*`
        },
        pageSize      : 100,
        includeGroups : true,
        accountId
      });
      assets = results?.assets;
      if (assets?.length) {
        getGroupFromEntities(assets, false);
      } else {
        setShowGroupsError(true);
      }
    } catch (err) {
      setShowGroupsError(true);
    }
  }, [accountId, identifierType]);

  const getGroupFromEntities = useCallback((selectedData, isDeviceIdentifiers) => {
    const removeGroupList = [];
    const entityGroups = isDeviceIdentifiers ? groupsList(selectedData) : flattenGroupsList(selectedData);
    const uniqueGroups = getUniqGroups(entityGroups);
    uniqueGroups?.forEach(item => {
      removeGroupList.push({
        ...item,
        name : item?.title,
        id   : entityId(item)
      });
    });
    const removeGroupLists = sortByString(removeGroupList, 'name');
    setReplaceGroups(removeGroupLists);
  }, [groupAction]);

  const groupsList = (deviceList: GenericObject[]) => {
    let list = [];
    deviceList?.forEach?.(devices => {
      const {device} = devices;
      if (device?.groups) {
        list = list.concat(device?.groups);
      }
    });
    return list;
  };

  const checkGroups = useCallback(() => {
    let noGroup = false;
    if (groupAction !== GroupAssignmentActions.Add) {
      noGroup = !replaceGroups?.length;
    }
    setNoGroups(noGroup);
    setShowGroupsError(true);
  }, [groupAction, replaceGroups]);

  const onDismiss = useCallback(() => {
    setShowGroupsError(false);
  }, []);

  const handleSubmit = (details: GroupAssignmentDetails) => {
    const createNewGroup = (details) => {
      createGroup({
        account : setRel(account, 'account'),
        name    : formState[GroupAssignmentDetailsFields.NAME]?.value,
        notes   : formState[GroupAssignmentDetailsFields.NOTES]?.value
      }, {
        onSuccess : (newGroupId) => {
          let isValid = false;
          if (isAdd(groupAction)) {
            isValid = details.addToGroup?.length > 0;
            onSave({
              addToGroup : isValid ? [newGroupId, ...details.addToGroup] : [newGroupId]
            });
          }
          if (isReplace(groupAction)) {
            const existingFrom = details?.replaceGroup?.[0]?.from;
            onSave({
              replaceGroup : [{
                from : existingFrom,
                to   : newGroupId
              }]
            });
          }
        },
        onError : (err) => {
          error('Unable to create new group due to: ', err);
        }
      });
    };

    if (formState[GroupAssignmentDetailsFields.NAME]?.required) {
      createNewGroup(details);
    } else {
      let isEmpty = false;
      const serializedDetails: GroupAssignmentDetails = {};
      if (isAdd(groupAction)) {
        isEmpty = validateGroup(details.addToGroup, false);
        serializedDetails.addToGroup = details.addToGroup;
      } else if (isRemove(groupAction)) {
        if (details.removeAllGroups) {
          isEmpty = false;
          serializedDetails.removeAllGroups = true;
        } else {
          isEmpty = validateGroup(details.removeFromGroup, false);
          serializedDetails.removeFromGroup = details.removeFromGroup;
        }
      } else if (isReplace(groupAction)) {
        isEmpty = validateGroup(details.replaceGroup, true);
        serializedDetails.replaceGroup = details.replaceGroup;
      }
      if (!isEmpty) {
        onSave(serializedDetails);
      }
    }
  };

  const validateGroup = useCallback((action, isReplace) => {
    if (isUndefined(action[0])) {
      setIsGroupEmpty(true);
      return true;
    }
    if (isReplace) {
      if (!isUndefined(action[0]?.from) && !isUndefined(action[0]?.to)) {
        return false;
      }
      isUndefined(action[0]?.from) ? setIsFromGroupEmpty(true) : setIsFromGroupEmpty(false);
      isUndefined(action[0]?.to) ? setIsGroupEmpty(true) : setIsGroupEmpty(false);
      return true;
    }
  }, [isGroupEmpty]);

  const onSubmit = useFormSave({
    onSave : handleSubmit,
    formState,
    serialize,
    setFormState
  });

  useImperativeHandle(ref, () => ({
    onSubmit
  }), [onSubmit]);

  const {
    groupsResult,
    groups,
    areGroupsLoading
  } = useGroupAssignmentDetailsForm(entityId(formState[GroupAssignmentDetailsFields.ACCOUNT]?.value || account));

  return (
    <>
      <EmbeddedForm>
        <FormItem fullWidth>
          <GroupAssignmentActionChooser handleActionChooser={handleActionChooser}
            groupAction={groupAction} />
          <GroupAssignmentAccountChooser groupAction={groupAction}
            groupsResult={groupsResult}
            groups={groups}
            areGroupsLoading={areGroupsLoading}
            account={globalGroup}
            isNewGroup={isNewGroup}
            handleRemoveGroupChooser={handleRemoveGroupChooser}
            handleFromGroupChooser={handleFromGroupChooser}
            fromGroup={fromGroup}
            toGroup={toGroup}
            handleAccountGroupChooser={handleAccountGroupChooser}
            handleGroupChooser={handleGroupChooser}
            replaceGroups={replaceGroups}
            isTotalRowsSelected={areTotalRowsSelected}
            isSelectedGridList={isSelectedGridList}
            isGroupNameEmpty={isGroupNameEmpty}
            isGroupNameExists={isGroupNameExists}
            groupName={groupName}
            groupNotes={groupNotes}
            formState={formState}
            fromAccount={fromAccount}
            handleGroupNameChooser={handleGroupNameChooser}
            handleGroupNotesChooser={handleGroupNotesChooser}
            isFromGroupEmpty={isFromGroupEmpty} />
        </FormItem>
        {isGroupEmpty && (
          <span className={classes.errorMessage}>{translate('common:editors.shared.required')}</span>
        )}
      </EmbeddedForm>
      {!areTotalRowsSelected && noGroups && (
        <UserNotificationDialog show={showGroupsError}
          capitalize
          title={translate('common:editors.bulkActions.groupAssignment.information')}
          message={translate('common:editors.bulkActions.groupAssignment.noGroupsError', {action: groupAction})}
          dismissLabel={translate('common:ok')}
          onDismiss={onDismiss} />
      )}
    </>
  );
});

export const isAdd     = (action: GroupAssignmentActions): boolean => isEqual(action, GroupAssignmentActions.Add);
export const isReplace = (action: GroupAssignmentActions): boolean => isEqual(action, GroupAssignmentActions.Replace);
export const isRemove  = (action: GroupAssignmentActions): boolean => isEqual(action, GroupAssignmentActions.Remove);

export const getValidIds = (action, formState) => (
  Array.isArray(formState[action]?.value) && formState[action]?.value?.length
    ? formState[action]?.value.filter(({id}) => !!id).map(({id}) => id) : [formState[action]?.value?.id]
);

const serialize = (formState: FormState): GroupAssignmentDetails => {
  const {
    ADD_GROUP,
    REMOVE_GROUP,
    REPLACE_FROM_GROUP,
    REPLACE_TO_GROUP
  } = GroupAssignmentDetailsFields;
  const removeAllGroup = translate('common:editors.bulkActions.groupAssignment.removeAllGroup');
  return {
    addToGroup      : getValidIds(ADD_GROUP, formState),
    removeAllGroups : Array.isArray(formState[REMOVE_GROUP]?.value)
      ? !!formState[REMOVE_GROUP]?.value?.find(group => group.name === removeAllGroup) : false,
    removeFromGroup : getValidIds(REMOVE_GROUP, formState),
    replaceGroup    : [{
      from : formState[REPLACE_FROM_GROUP]?.value?.id,
      to   : formState[REPLACE_TO_GROUP]?.value?.id
    }]
  };
};

export default memo(GroupAssignmentDetailsForm);
